import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Composant pour le slider Avant/Après
const BeforeAfterSlider = () => {
    const [sliderPosition, setSliderPosition] = useState(50);
    const containerRef = useRef(null);
    const isDragging = useRef(false);

    const handleMouseDown = () => {
        isDragging.current = true;
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current || !containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
            setSliderPosition(Math.max(0, Math.min(100, newPosition)));
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // Auto-animate slider
    useEffect(() => {
        let animationId;
        let direction = 1;
        let position = 50;

        const animate = () => {
            position += direction * 0.5;
            if (position >= 80) direction = -1;
            if (position <= 20) direction = 1;
            setSliderPosition(position);
            animationId = requestAnimationFrame(animate);
        };

        animationId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationId);
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative w-full max-w-2xl h-48 sm:h-64 md:h-80 lg:h-96 rounded-2xl overflow-hidden cursor-ew-resize shadow-2xl mx-auto"
            onMouseDown={handleMouseDown}
        >
            {/* Avant - Ancien site (design basique mais terne) */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-200 via-yellow-100 to-orange-100 overflow-hidden">
                {/* Patterns simples mais visuellement faibles */}
                <div className="absolute inset-0">
                    {/* Bandes colorées passées */}
                    <div className="absolute top-0 left-0 right-0 h-16 sm:h-20 bg-gradient-to-r from-orange-400 to-yellow-300 opacity-60"></div>
                    <div className="absolute top-16 sm:top-24 left-0 right-0 h-12 sm:h-16 bg-gradient-to-r from-red-300 to-pink-200 opacity-50"></div>
                    
                    {/* Formes simples, peu attrayantes */}
                    <div className="absolute top-8 sm:top-12 left-4 sm:left-8 w-16 sm:w-24 h-16 sm:h-24 bg-yellow-300/40 border-2 border-orange-300 rounded-sm"></div>
                    <div className="absolute top-28 sm:top-40 right-6 sm:right-12 w-14 sm:w-20 h-14 sm:h-20 bg-orange-300/30 border-2 border-yellow-400 rounded-sm"></div>
                    <div className="absolute bottom-12 sm:bottom-16 left-1/4 w-24 sm:w-32 h-2 sm:h-3 bg-orange-400/50 rounded-full"></div>
                    
                    {/* Texte simple et sans style */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-0 pointer-events-none">
                        <div className="text-5xl mb-2">�</div>
                        <h3 className="text-2xl font-bold text-orange-700 drop-shadow-sm">
                            Site Ancien
                        </h3>
                        <p className="text-sm text-orange-600 mt-2 opacity-80">
                            Basique • Peu attrayant • Dépassé
                        </p>
                    </div>

                    {/* Quelques lignes de séparation */}
                    <div className="absolute left-0 right-0 top-1/3 h-0.5 bg-orange-300 opacity-40"></div>
                    <div className="absolute left-0 right-0 top-2/3 h-0.5 bg-yellow-300 opacity-30"></div>
                </div>
            </div>

            {/* Après - Site refonte (design magnifique) */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-[#2f3b52] via-[#3d4e68] to-[#4a5a7d] overflow-hidden"
                style={{
                    width: `${sliderPosition}%`,
                    transition: isDragging.current ? 'none' : 'width 0.05s ease-out'
                }}
            >
                <div className="w-full h-full flex flex-col items-center justify-center p-4 sm:p-8">
                    <div className="text-white text-center">
                        <div className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">✨</div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">Site Refonte</h3>
                        <p className="text-xs sm:text-sm opacity-90">Design moderne • Responsive • Rapide</p>
                        <div className="mt-4 sm:mt-6 space-y-1 sm:space-y-2">
                            <div className="w-32 sm:w-40 h-2 sm:h-3 bg-[#f7876f] rounded mx-auto"></div>
                            <div className="w-36 sm:w-48 h-2 sm:h-3 bg-[#f7876f] rounded mx-auto"></div>
                            <div className="w-28 sm:w-36 h-2 sm:h-3 bg-[#f7876f] rounded mx-auto"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Slider handle */}
            <div
                className="absolute top-0 h-full w-0.5 sm:w-1 bg-white/80 shadow-lg"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
                {/* Arrows */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 sm:w-12 h-8 sm:h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-4 sm:w-6 h-4 sm:h-6 text-[#2f3b52]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.5 7a1 1 0 11-2 0 1 1 0 012 0zm6 0a1 1 0 11-2 0 1 1 0 012 0z" />
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>

            {/* Labels */}
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4 text-white font-bold text-xs sm:text-sm bg-black/40 px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg">
                APRÈS
            </div>
            <div className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white font-bold text-xs sm:text-sm bg-black/40 px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg">
                AVANT
            </div>
        </div>
    );
};

// Hook personnalisé pour détecter quand un élément est visible
const useScrollAnimation = () => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const currentRef = ref.current; // Copie pour le cleanup
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Une fois visible, on ne cache plus
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    return [ref, isVisible];
};

// Hook pour animer les compteurs de nombres
const useCountUp = (end, duration = 2000, isVisible) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!isVisible) return;

        let startTime;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percentage = Math.min(progress / duration, 1);
            
            // Fonction d'easing pour une animation plus fluide
            const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
            setCount(Math.floor(easeOutQuart * end));

            if (percentage < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [end, duration, isVisible]);

    return count;
};

// Composant pour l'effet Matrix
const MatrixBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
        const charArray = chars.split('');
        
        const drops = [];
        const columns = Math.floor(canvas.width / 15);
        
        for (let i = 0; i < columns; i++) {
            drops[i] = Math.random() * canvas.height;
        }

        const draw = () => {
            ctx.fillStyle = 'rgba(47, 59, 82, 0.05)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = 'rgba(34, 197, 94, 0.3)';
            ctx.font = 'bold 14px Courier New';

            for (let i = 0; i < drops.length; i++) {
                const char = charArray[Math.floor(Math.random() * charArray.length)];
                ctx.fillText(char, i * 15, drops[i]);

                if (drops[i] * Math.random() > 0.975) {
                    drops[i] = 0;
                } else {
                    drops[i]++;
                }
            }
        };

        const interval = setInterval(draw, 50);
        return () => clearInterval(interval);
    }, []);

    return <canvas ref={canvasRef} className="absolute inset-0 opacity-50" />;
};

const Home = () => {
    const [servicesRef, servicesVisible] = useScrollAnimation();
    const [statsRef, statsVisible] = useScrollAnimation();
    const [portfolioRef, portfolioVisible] = useScrollAnimation();
    const [pricingRef, pricingVisible] = useScrollAnimation();
    const [ctaRef, ctaVisible] = useScrollAnimation();

    // Compteurs animés pour les stats
    const projectsCount = useCountUp(55, 4000, statsVisible);
    const clientsCount = useCountUp(30, 2600, statsVisible);

    // État pour le chat widget
    const [isChatOpen, setIsChatOpen] = useState(false); // Ouvert par défaut
    const [chatForm, setChatForm] = useState({
        name: '',
        message: ''
    });
    const [chatSubmitted, setChatSubmitted] = useState(false);

    const handleChatSubmit = (e) => {
        e.preventDefault();
        // Ici vous pouvez ajouter l'envoi par email ou sauvegarde en DB
        console.log('Message chat:', chatForm);
        setChatSubmitted(true);
        
        // Redirection WhatsApp avec le message
        const whatsappMessage = `Bonjour! Je suis ${chatForm.name}. ${chatForm.message}`;
        const whatsappUrl = `https://wa.me/221779317865?text=${encodeURIComponent(whatsappMessage)}`;
        window.open(whatsappUrl, '_blank');
        
        // Réinitialiser après 3 secondes
        setTimeout(() => {
            setIsChatOpen(false);
            setChatSubmitted(false);
            setChatForm({ name: '', message: '' });
        }, 3000);
    };

    const [scrollProgress, setScrollProgress] = useState(0);
    const heroRef = useRef(null);

    // Liquid Swipe Effect
    useEffect(() => {
        const handleScroll = () => {
            if (heroRef.current) {
                const rect = heroRef.current.getBoundingClientRect();
                const progress = Math.max(0, Math.min(1, 1 - (rect.bottom / window.innerHeight)));
                setScrollProgress(progress);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="bg-gradient-to-br from-slate-50 via-white to-slate-100">
                        {/* Hero Section - White Fluid Layout */}
                        <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden py-14 md:py-20 px-4 bg-white">
                                {/* Fond blanc fluide */}
                                <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fdfcfb] to-[#f8fafc]"></div>

                                <div
                                        className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl bg-[#f7876f]/20"
                                        style={{
                                                transform: `translate(${scrollProgress * 30}px, ${scrollProgress * 20}px)`,
                                                transition: 'transform 0.1s ease-out'
                                        }}
                                ></div>
                                <div
                                        className="absolute top-1/3 -right-20 w-[28rem] h-[28rem] rounded-full blur-3xl bg-[#2f3b52]/10"
                                        style={{
                                                transform: `translate(${-scrollProgress * 25}px, ${scrollProgress * 15}px)`,
                                                transition: 'transform 0.1s ease-out'
                                        }}
                                ></div>

                                <svg className="absolute inset-0 w-full h-full opacity-70" preserveAspectRatio="none" viewBox="0 0 1200 800" aria-hidden="true">
                                        <path
                                                d="M0,180 C220,130 360,220 540,180 C760,130 980,240 1200,170 L1200,800 L0,800 Z"
                                                fill="rgba(247,135,111,0.08)"
                                                style={{
                                                        transform: `translateY(${scrollProgress * 25}px)`,
                                                        transition: 'transform 0.1s ease-out'
                                                }}
                                        />
                                        <path
                                                d="M0,540 C250,480 410,620 620,560 C860,500 980,620 1200,560 L1200,800 L0,800 Z"
                                                fill="rgba(47,59,82,0.07)"
                                                style={{
                                                        transform: `translateY(${-scrollProgress * 20}px)`,
                                                        transition: 'transform 0.1s ease-out'
                                                }}
                                        />
                                </svg>

                                <div className="max-w-7xl mx-auto w-full relative z-10">
                                        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                                                {/* Colonne gauche: texte + CTA */}
                                                <div
                                                        className="text-center lg:text-left"
                                                        style={{
                                                                transform: `translateY(${scrollProgress * -12}px)`,
                                                                transition: 'transform 0.1s ease-out'
                                                        }}
                                                >
                                                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f7876f]/10 text-[#d96049] text-xs sm:text-sm font-semibold border border-[#f7876f]/30 mb-4">
                                                        WebAura Studio
                                                    </span>

                                                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#2f3b52] mb-4 leading-[1.05]">
                                                        Solutions digitales pour
                                                        <span className="block text-[#f7876f]">PME et particuliers</span>
                                                    </h1>

                                                    <p className="text-base sm:text-lg md:text-xl text-[#3d4e68] leading-relaxed max-w-2xl mx-auto lg:mx-0">
                                                        Sites vitrines, e-commerce et outils sur mesure pour accelerer votre croissance en ligne avec un design moderne, rapide et efficace.
                                                    </p>

                                                    <div className="mt-5 flex flex-wrap gap-2 justify-center lg:justify-start">
                                                        <span className="px-3 py-1.5 rounded-full bg-white border border-[#2f3b52]/15 text-[#2f3b52] text-xs font-medium">Design premium</span>
                                                        <span className="px-3 py-1.5 rounded-full bg-white border border-[#2f3b52]/15 text-[#2f3b52] text-xs font-medium">Performance SEO</span>
                                                        <span className="px-3 py-1.5 rounded-full bg-white border border-[#2f3b52]/15 text-[#2f3b52] text-xs font-medium">Accompagnement humain</span>
                                                    </div>

                                                        {/* CTA alignes a gauche en desktop */}
                                                        <div className="mt-8 flex flex-col sm:flex-row lg:flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
                                                                <Link
                                                                        to="/order"
                                                                        className="bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white px-8 sm:px-10 py-3.5 rounded-lg text-sm sm:text-base font-bold hover:from-[#e97058] hover:to-[#d96049] transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-[#f7876f]/40 text-center"
                                                                >
                                                                        Commander Maintenant
                                                                </Link>

                                                                <a
                                                                        href="#pricing"
                                                                        className="bg-white text-[#2f3b52] border-2 border-[#2f3b52]/20 px-6 py-3.5 rounded-lg text-sm font-semibold hover:bg-[#2f3b52] hover:text-white transition-all duration-200 shadow-sm text-center"
                                                                >
                                                                        Tarifs
                                                                </a>
                                                                
                                                        </div>
                                                </div>

                                                {/* Colonne droite: image hero */}
                                                <div
                                                    className="relative flex items-center justify-center"
                                                        style={{
                                                                transform: `translateY(${scrollProgress * 8}px)`,
                                                                transition: 'transform 0.1s ease-out'
                                                        }}
                                                >
                                                    <img
                                                        src="/images/portfolio/heroimg2.png"
                                                        alt="Illustration hero WebAura"
                                                        className="w-full max-w-[560px] lg:max-w-[620px] h-[380px] sm:h-[360px] lg:h-[500px] object-contain"
                                                    />
                                                </div>
                                        </div>
                                </div>
                        </section>


            {/* Services Section */}
            <section ref={servicesRef} className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <h2 className={`text-4xl md:text-5xl font-bold text-center text-[#2f3b52] mb-16 transition-all duration-1000 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Nos Services
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { 
                                title: 'Site Web', 
                                desc: 'Présence en ligne professionnelle et performante pour votre entreprise',
                                color: 'from-blue-500 to-cyan-500',
                                icon: 'M9 19V6a2 2 0 012-2h6a2 2 0 012 2v13a2 2 0 01-2 2H11a2 2 0 01-2-2zm0 0V9a2 2 0 012-2h6a2 2 0 012 2v10a2 2 0 01-2 2H11a2 2 0 01-2-2z'
                            },
                            { 
                                title: 'E-commerce', 
                                desc: 'Boutique en ligne sécurisée pour vendre vos produits 24h/24',
                                color: 'from-green-500 to-emerald-500',
                                icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
                            },
                            { 
                                title: 'Cybersécurité', 
                                desc: 'Protection avancée contre les menaces numériques et conformité RGPD',
                                color: 'from-red-500 to-pink-500',
                                icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                            },
                            { 
                                title: 'Solutions IA', 
                                desc: 'Intelligence artificielle pour automatiser et optimiser vos processus',
                                color: 'from-purple-500 to-indigo-500',
                                icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'
                            },
                            { 
                                title: 'Solutions IoT', 
                                desc: 'Connectez vos appareils et exploitez les données en temps réel',
                                color: 'from-green-500 to-teal-500',
                                icon: 'M9 12l2 2m4-4l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z'
                            },
                            { 
                                title: 'Refonte Web', 
                                desc: 'Modernisez votre site existant avec design contemporain et UX optimisée',
                                color: 'from-orange-500 to-yellow-500',
                                icon: 'M14.828 14.828a4 4 0 01-5.656 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9 9h.01M15 9h.01'
                            },
                            { 
                                title: 'Sur Mesure', 
                                desc: 'Solutions personnalisées adaptées à vos besoins spécifiques et complexes',
                                color: 'from-indigo-500 to-blue-500',
                                icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M12 15a3 3 0 100-6 3 3 0 000 6z'
                            }
                        ].map((service, index) => (
                            <div 
                                key={index}
                                className={`group relative bg-white rounded-xl shadow-md hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-300 border border-gray-200 hover:border-[#f7876f] overflow-hidden ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                {/* Gradient top bar */}
                                <div className={`h-1 w-full bg-gradient-to-r ${service.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
                                
                                {/* Icon container */}
                                <div className={`w-16 h-16 mx-auto mt-6 mb-4 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={service.icon} />
                                    </svg>
                                </div>
                                
                                {/* Content */}
                                <div className="p-6 text-center">
                                    <h3 className="text-xl font-bold text-[#2f3b52] mb-3 group-hover:text-[#f7876f] transition-colors duration-300">{service.title}</h3>
                                    <p className="text-[#2f3b52] leading-relaxed text-sm">{service.desc}</p>
                                </div>
                                
                                {/* Hover effect bottom */}
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#f7876f] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section ref={statsRef} className="py-20 px-4 bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px'}}></div>
                </div>
                <div className="max-w-6xl mx-auto relative z-10">
                    <div className={`text-center mb-12 transition-all duration-1000 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Notre Expérience en Chiffres
                        </h2>
                        <p className="text-xl text-gray-200">
                            Des résultats concrets qui parlent d'eux-mêmes
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        <div className={`bg-white/10 backdrop-blur-sm rounded-lg p-10 border border-white/20 transform hover:scale-105 transition-all duration-200 ${statsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{ transitionDelay: '200ms' }}>
                            <div className="text-center">
                                <div className="text-7xl md:text-8xl font-bold text-[#f7876f] mb-4">
                                    +{projectsCount}
                                </div>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                    Projets Réalisés
                                </h3>
                                <p className="text-gray-200 text-lg">
                                    Sites web, e-commerce et applications livrés avec succès
                                </p>
                            </div>
                        </div>
                        <div className={`bg-white/10 backdrop-blur-sm rounded-lg p-10 border border-white/20 transform hover:scale-105 transition-all duration-200 ${statsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{ transitionDelay: '400ms' }}>
                            <div className="text-center">
                                <div className="text-7xl md:text-8xl font-bold text-green-400 mb-4">
                                    +{clientsCount}
                                </div>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                    Clients Satisfaits
                                </h3>
                                <p className="text-gray-200 text-lg">
                                    Entrepreneurs et entreprises qui nous font confiance
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={`mt-12 text-center transition-all duration-1000 ${statsVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: '600ms' }}>
                        <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
                            <span className="text-3xl">✓</span>
                            <span className="text-white font-semibold text-lg">100% de satisfaction client</span>
                            <span className="text-3xl">✓</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio Section - Nos Réalisations */}
            <section ref={portfolioRef} id="portfolio" className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className={`text-center mb-16 transition-all duration-1000 ${portfolioVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold text-[#2f3b52] mb-4">
                            Nos Réalisations
                        </h2>
                        <p className="text-xl text-[#2f3b52] max-w-2xl mx-auto">
                            Découvrez quelques-uns des projets que nous avons réalisés avec passion
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                title: 'Village Artisanal',
                                url: 'https://village-artisanal.sn',
                                description: 'Site e-commerce pour promotion de l\'artisanat sénégalais',
                                category: 'SAAS pour Artisanat',
                                color: 'from-blue-500 to-blue-600'
                            },
                            {
                                title: 'QR Code Generator',
                                url: 'https://qrcode-zeta-murex.vercel.app/',
                                description: 'Application web de génération de QR codes',
                                category: 'Générateur de QR Code',
                                color: 'from-purple-500 to-indigo-600'
                            },
                            {
                                title: 'Aar Mbay Mi',
                                url: 'https://aar-mbay-mi.vercel.app',
                                description: 'Plateforme blog de la startup Aar Mbay Mi',
                                category: 'Site Blog',
                                color: 'from-green-500 to-emerald-600'
                            },
                            {
                                title: 'Server Ping Monitor',
                                url: 'https://server-ping-frontend.vercel.app/',
                                description: 'Outil de monitoring et surveillance de serveurs',
                                category: 'Application Web',
                                color: 'from-red-500 to-rose-600'
                            },
                            {
                                title: 'Jamaal Store',
                                url: 'https://jamaal-store.vercel.app/',
                                description: 'Boutique en ligne pour parfums et cosmétiques',
                                category: 'E-commerce',
                                color: 'from-amber-500 to-orange-600'
                            },
                           
                            {
                                title: 'Samavote',
                                url: 'https://samavote-frontend.vercel.app/',
                                description: 'Plateforme de vote en ligne sécurisée et moderne',
                                category: 'Application Web',
                                color: 'from-pink-500 to-rose-600'
                            },
                             {
                                title: 'Koleurei',
                                url: 'https://koleurei-front.vercel.app/',
                                description: 'Plateforme de cartes de visite numériques (vCard)',
                                category: 'Application Web',
                                color: 'from-cyan-500 to-blue-600'
                            },
                            {
                                title: 'Zaza Parfumerie',
                                url: 'https://zazaparfumerie.vercel.app',
                                description: 'Boutique en ligne de parfums et produits de beauté',
                                category: 'E-commerce',
                                color: 'from-red-800 to-rose-900'
                            }
                        ].map((project, index) => (
                            <a 
                                key={index}
                                href={project.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`group relative bg-gradient-to-br from-slate-50 to-white rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-200 border border-gray-300 hover:border-[#f7876f] overflow-hidden ${portfolioVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                {/* Image avec capture d'écran du site */}
                                <div className={`relative h-64 bg-gradient-to-br ${project.color} overflow-hidden`}>
                                    {/* Vraie capture d'écran du site */}
                                    <img
                                        src={`/images/portfolio/${project.title.toLowerCase().replace(/\s+/g, '-')}.jpg`}
                                        alt={`Capture d'écran de ${project.title}`}
                                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                                        loading="lazy"
                                        onError={(e) => {
                                            // Si l'image locale n'existe pas, essayer avec un service de screenshot
                                            if (!e.target.dataset.tried) {
                                                e.target.dataset.tried = 'true';
                                                e.target.src = `https://shot.screenshotapi.net/screenshot?token=JQXNC3Q-MCSQVNJ-P4QPTFC-HVXPAGN&url=${encodeURIComponent(project.url)}&output=image&file_type=png&wait_for_event=load&delay=2000&width=1200&height=800`;
                                            } else if (!e.target.dataset.secondTried) {
                                                e.target.dataset.secondTried = 'true';
                                                // Essayer mini.s-shot.ru (gratuit)
                                                e.target.src = `https://mini.s-shot.ru/1024x768/JPEG/1024/Z100/?${project.url}`;
                                            } else {
                                                // Dernier fallback: afficher un design avec le nom
                                                e.target.style.display = 'none';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback) fallback.classList.remove('hidden');
                                            }
                                        }}
                                    />
                                    
                                    {/* Fallback si l'image ne charge pas */}
                                    <div className={`hidden absolute inset-0 bg-gradient-to-br ${project.color} items-center justify-center flex-col p-6`}>
                                        <div className="text-center">
                                            <svg className="w-20 h-20 text-white/80 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <h3 className="text-2xl font-bold text-white mb-2">{project.title}</h3>
                                            <p className="text-white/80 text-sm">{project.category}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Overlay sombre */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                    
                                    {/* Badge catégorie en haut à droite */}
                                    <div className="absolute top-4 right-4 z-10">
                                        <span className={`inline-block bg-gradient-to-r ${project.color} text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg backdrop-blur-sm`}>
                                            {project.category}
                                        </span>
                                    </div>
                                    
                                    {/* Overlay au survol avec bouton */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100">
                                            <div className="bg-white text-[#2f3b52] px-8 py-4 rounded-lg flex items-center gap-3 font-bold text-lg shadow-2xl">
                                                <span>Visiter le site</span>
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-xl font-bold text-[#2f3b52] group-hover:text-[#f7876f] transition-colors">
                                            {project.title}
                                        </h3>
                                        <span className={`text-xs font-semibold px-3 py-1 rounded-lg bg-gradient-to-r ${project.color} text-white`}>
                                            {project.category}
                                        </span>
                                    </div>
                                    <p className="text-[#2f3b52] leading-relaxed text-sm">
                                        {project.description}
                                    </p>
                                </div>

                                {/* Bottom decoration */}
                                <div className={`h-1 bg-gradient-to-r ${project.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200`}></div>
                            </a>
                        ))}
                    </div>

                    {/* Call to action */}
                    <div className="mt-16 text-center">
                        <p className="text-xl text-[#2f3b52] mb-6">
                            Vous voulez un site comme ceux-ci ?
                        </p>
                        <Link
                            to="/order"
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-[#3d4e68] hover:to-[#4a5a7d] transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <span>Démarrer votre projet</span>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section ref={pricingRef} id="pricing" className="py-24 px-4 bg-gradient-to-br from-[#2f3b52] to-[#3d4e68] relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
                </div>
                
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className={`text-center mb-16 transition-all duration-1000 ${pricingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Nos Tarifs
                        </h2>
                        <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                            Des solutions adaptées à tous les budgets avec un excellent rapport qualité-prix
                        </p>
                        <p className="text-sm text-gray-300 mt-3">💰 Tous nos tarifs incluent une année complète</p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 items-stretch">
                        {[
                            {
                                title: 'Site Vitrine',
                                price: '195.000',
                                priceUnit: 'FCFA/an',
                                description: 'Parfait pour les petites et moyennes entreprises',
                                features: [
                                    'Design moderne et élégant',
                                    'Responsive (mobile, tablette, PC)',
                                    'SEO optimisé',
                                    'Hébergement gratuit 1 an',
                                    'Certificat SSL inclus',
                                    '1 adresse email professionnelle',
                                    'Support technique 6 mois',
                                    'Mise à jour de contenu (3 mois)',
                                    'Livraison sous 1 semaine'
                                ],
                                color: 'from-blue-500 to-blue-600'
                            },
                            {
                                title: 'Blog Professionnel',
                                price: '264.000',
                                priceUnit: 'FCFA/an',
                                description: 'Idéal pour partager votre expertise',
                                features: [
                                    'Design personnalisé unique',
                                    'Système de blog avancé',
                                    'Gestion des commentaires',
                                    'Newsletter intégrée',
                                    'Optimisation SEO avancée',
                                    'Partage sur réseaux sociaux',
                                    '2 adresses email professionnelles',
                                    'Support prioritaire 9 mois',
                                    'Formation à la rédaction web',
                                    'Livraison sous 2 semaines'
                                ],
                                color: 'from-purple-500 to-indigo-600'
                            },
                            {
                                title: 'E-commerce',
                                price: '300.000',
                                priceUnit: 'FCFA/an',
                                description: 'Solution complète pour vendre en ligne',
                                features: [
                                    'Toutes fonctionnalités Site Vitrine',
                                    'Panier d\'achat intégré',
                                    'Paiement en ligne sécurisé',
                                    'Gestion complète des produits',
                                    'Tableau de bord administrateur',
                                    'Gestion des stocks',
                                    '3 adresses email professionnelles',
                                    'Formation complète incluse',
                                    'Support prioritaire 1 an',
                                    'Livraison express sous 3 semaines'
                                ],
                                popular: true,
                                color: 'from-amber-500 to-orange-600'
                            },
                            {
                                title: 'Site Personnalisé',
                                price: 'Sur Devis',
                                priceUnit: '(selon votre projet)',
                                description: 'Solution sur-mesure adaptée à vos besoins',
                                features: [
                                    'Analyse approfondie de vos besoins',
                                    'Design 100% personnalisé',
                                    'Fonctionnalités sur-mesure',
                                    'Architecture évolutive',
                                    'Intégrations tierces avancées',
                                    'Accompagnement personnalisé',
                                    'Support technique dédié',
                                    'Formation complète de l\'équipe',
                                    'Maintenance et mises à jour',
                                    'Délai selon cahier des charges'
                                ],
                                color: 'from-green-500 to-teal-600'
                            }
                        ].map((plan, index) => (
                            <div 
                                key={index}
                                className={`relative bg-white rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 hover:scale-105 hover:shadow-xl flex flex-col ${
                                    plan.popular ? 'lg:scale-105 ring-2 ring-[#f7876f]' : ''
                                } ${pricingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${index * 150}ms` }}
                            >
                                {plan.popular && (
                                    <div className={`absolute top-0 left-0 right-0 bg-gradient-to-r ${plan.color} text-white py-3 text-center font-bold text-sm tracking-wider`}>
                                        LE PLUS POPULAIRE
                                    </div>
                                )}
                                
                                <div className={`${plan.popular ? 'pt-16' : 'pt-8'} px-6 md:px-8 pb-6 md:pb-8 flex flex-col h-full`}>
                                    {/* Header */}
                                    <div className="text-center mb-4 md:mb-6">
                                        <h3 className="text-2xl md:text-3xl font-bold text-[#2f3b52] mb-2">{plan.title}</h3>
                                        <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">{plan.description}</p>
                                        <div className={`inline-block bg-gradient-to-r ${plan.color} text-white px-4 md:px-6 py-2 md:py-3 rounded-lg shadow-lg`}>
                                            <span className="text-3xl md:text-4xl font-bold">{plan.price}</span>
                                            {plan.priceUnit && (
                                                <div className="text-xs md:text-sm mt-1">{plan.priceUnit}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t-2 border-gray-200 my-4 md:my-6"></div>

                                    {/* Features */}
                                    <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8 flex-grow">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start group">
                                                <div className={`flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center mr-2 md:mr-3 mt-0.5 group-hover:scale-110 transition-transform`}>
                                                    <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <span className="text-gray-700 text-xs md:text-sm leading-relaxed">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA Button */}
                                    <Link
                                        to="/order"
                                        className={`block text-center py-3 md:py-4 px-4 md:px-6 rounded-lg font-bold text-base md:text-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl mt-auto ${
                                            plan.popular 
                                                ? `bg-gradient-to-r ${plan.color} text-white` 
                                                : 'bg-[#2f3b52] text-white hover:bg-[#3d4e68]'
                                        }`}
                                    >
                                        Choisir ce plan →
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Additional Info */}
                    <div className="mt-16 text-center">
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 max-w-4xl mx-auto border border-white/20">
                            <h3 className="text-2xl font-bold text-white mb-4">Inclus dans tous les forfaits</h3>
                            <div className="grid md:grid-cols-3 gap-6 text-white">
                                <div className="flex items-center justify-center space-x-2">
                                    <span className="text-2xl">✓</span>
                                    <span>Déploiement rapide</span>
                                </div>
                                <div className="flex items-center justify-center space-x-2">
                                    <span className="text-2xl">✓</span>
                                    <span>Sécurité garantie</span>
                                </div>
                                <div className="flex items-center justify-center space-x-2">
                                    <span className="text-2xl">✓</span>
                                    <span>Support réactif</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section ref={ctaRef} className="py-24 px-4 bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] text-white border-t-4 border-[#f7876f] relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f7876f]/10 to-transparent"></div>
                </div>
                <div className={`max-w-4xl mx-auto text-center relative z-10 transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                        Prêt à démarrer votre projet ?
                    </h2>
                    <p className="text-xl md:text-2xl mb-12 text-gray-200">
                        Contactez-nous dès aujourd'hui et donnons vie à vos idées
                    </p>
                    <Link
                        to="/order"
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white px-12 py-5 rounded-lg text-xl font-bold hover:from-[#e97058] hover:to-[#d96049] transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-[#f7876f]/40"
                    >
                        <span>Passer ma commande</span>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </Link>
                    <p className="mt-6 text-blue-200 text-sm">
                        ✨ Réponse sous 24h • Devis gratuit • Consultation offerte
                    </p>
                </div>
            </section>

            {/* Chat Widget */}
            <div className="fixed bottom-6 right-6 z-50">
                {/* Bouton d'ouverture du chat */}
                {!isChatOpen && (
                    <button
                        onClick={() => setIsChatOpen(true)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-2xl hover:shadow-blue-500/50 transform hover:scale-110 transition-all duration-300 group relative"
                    >
                        {/* Effet de pulsation */}
                        <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75"></div>
                        
                        {/* Icône Chat */}
                        <svg className="w-9 h-9 md:w-11 md:h-11 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>

                        {/* Badge notification */}
                        <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center animate-bounce">
                            💬
                        </div>

                        {/* Tooltip */}
                        <div className="absolute right-20 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                            <span className="font-semibold">💬 Besoin d'aide ?</span>
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full border-8 border-transparent border-l-gray-900"></div>
                        </div>
                    </button>
                )}

                {/* Fenêtre du chat */}
                {isChatOpen && (
                    <div className="bg-white rounded-2xl shadow-2xl w-80 md:w-96 overflow-hidden animate-slideUp">
                        {/* En-tête */}
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                        <span className="text-2xl">👋</span>
                                    </div>
                                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                                </div>
                                <div>
                                    <h3 className="font-bold">WebAura</h3>
                                    <p className="text-xs text-blue-100">En ligne • Réponse rapide</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsChatOpen(false)}
                                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Corps du chat */}
                        <div className="p-4 bg-gray-50 max-h-96 overflow-y-auto">
                            {!chatSubmitted ? (
                                <>
                                    {/* Message de bienvenue */}
                                    <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-md mb-4 animate-fadeIn">
                                        <p className="text-gray-800 text-sm">
                                            👋 <strong>Bonjour !</strong> Bienvenue chez WebAura.<br/><br/>
                                            Comment pouvons-nous vous aider aujourd'hui ? Parlez-nous de votre projet ! 🚀
                                        </p>
                                    </div>

                                    {/* Suggestions rapides */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <button 
                                            onClick={() => setChatForm({...chatForm, message: 'Je veux un site vitrine'})}
                                            className="text-xs bg-white hover:bg-blue-50 text-blue-600 px-3 py-2 rounded-full shadow-sm border border-blue-200 transition-colors"
                                        >
                                            🌐 Site vitrine
                                        </button>
                                        <button 
                                            onClick={() => setChatForm({...chatForm, message: 'Je veux un site e-commerce'})}
                                            className="text-xs bg-white hover:bg-blue-50 text-blue-600 px-3 py-2 rounded-full shadow-sm border border-blue-200 transition-colors"
                                        >
                                            🛒 E-commerce
                                        </button>
                                        <button 
                                            onClick={() => setChatForm({...chatForm, message: 'J\'ai besoin d\'un devis'})}
                                            className="text-xs bg-white hover:bg-blue-50 text-blue-600 px-3 py-2 rounded-full shadow-sm border border-blue-200 transition-colors"
                                        >
                                            💰 Devis
                                        </button>
                                    </div>

                                    {/* Formulaire */}
                                    <form onSubmit={handleChatSubmit} className="space-y-3">
                                        <input
                                            type="text"
                                            placeholder="Votre nom *"
                                            required
                                            value={chatForm.name}
                                            onChange={(e) => setChatForm({...chatForm, name: e.target.value})}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                                        />
                                        <textarea
                                            placeholder="Votre message *"
                                            required
                                            rows="4"
                                            value={chatForm.message}
                                            onChange={(e) => setChatForm({...chatForm, message: e.target.value})}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all resize-none text-sm"
                                        ></textarea>
                                        <button
                                            type="submit"
                                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                                        >
                                            Envoyer 🚀
                                        </button>
                                    </form>
                                </>
                            ) : (
                                /* Message de confirmation */
                                <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-6 text-center animate-fadeIn">
                                    <div className="text-6xl mb-3">✅</div>
                                    <h4 className="font-bold text-green-800 text-lg mb-2">Message envoyé !</h4>
                                    <p className="text-green-700 text-sm">
                                        Nous vous recontactons sous 24h.<br/>
                                        Redirection vers WhatsApp...
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Pied de page */}
                        <div className="bg-gray-100 px-4 py-3 text-center border-t">
                            <p className="text-xs text-gray-600">
                                ⚡ Réponse sous 24h • 📞 +221 77 931 78 65
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Home;    